import { useEffect, useRef, useState } from 'react';
import { generateQRCode } from '@playcastdotio/Qrcode';
import {
  buildHandoffUrl,
  HANDOFF_QR_REFRESH_MS,
  type HandoffOriginDevice,
} from '../../utils/deviceHandoff';

interface UseHandoffQrCodeOptions {
  persistentId: string | undefined;
  originDeviceType?: HandoffOriginDevice;
  /** Optional logo rendered into the middle of the code. */
  logo?: string;
  /** Matched to the card so the code's own background does not read as a second panel. */
  backgroundColor?: string;
}

/**
 * Renders a QR encoding the campaign URL plus the visitor's persistent ID, and keeps it
 * fresh.
 *
 * The encoded bridge timestamp is only honoured for BRIDGE_EXPIRY_MS, so a code left on
 * screen would silently stop transferring identity and the scanning phone would be
 * counted as a brand-new visitor. Regenerating on an interval keeps any code currently
 * on screen scannable.
 */
export function useHandoffQrCode({
  persistentId,
  originDeviceType,
  logo,
  backgroundColor,
}: UseHandoffQrCodeOptions): { qrDataUrl: string | null } {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!persistentId) {
      return;
    }

    const refresh = async () => {
      const handoffUrl = buildHandoffUrl(persistentId, 'qr', originDeviceType);
      try {
        // The card around this code already carries a generous quiet zone, so the margin the
        // library leaves inside the image would only read as the code floating in its frame.
        const next = await generateQRCode(handoffUrl, {
          logo,
          backgroundColor,
          trimQuietZone: true,
          shape: 'square',
        });
        if (isMounted.current) {
          setQrDataUrl(next);
        }
      } catch (error) {
        console.warn('[DeviceHandoff] Failed to generate QR code:', error);
        if (isMounted.current) {
          setQrDataUrl(null);
        }
      }
    };

    void refresh();
    const intervalId = setInterval(() => void refresh(), HANDOFF_QR_REFRESH_MS);

    return () => clearInterval(intervalId);
  }, [persistentId, originDeviceType, logo, backgroundColor]);

  return { qrDataUrl };
}
